import type { RefAttributes } from "react";
import { Box, Flex, Text, Segment } from "@simplybusiness/mobius";
import type { PaymentInformationProps } from "..";
import { QuoteCardBadges } from "../../../../QuoteCardBadges";
import { QuoteCardBadge } from "../../../../QuoteCardBadges/ExperimentCancelAnytime";
import { ProductInfo } from "../../../../../../shared/ProductInfo";
import { transformCoverage } from "../../../../Coverage/transformCoverage";
import { CancelAnytime } from "../../../../../../shared/CancelAnytime";
import { useSnowplowContext } from "@simplybusiness/services";

export const PaymentInformation = ({
  ref,
  ...props
}: PaymentInformationProps & RefAttributes<HTMLDivElement>) => {
  const {
    quote: {
      installmentsAvailable,
      downpaymentAmount,
      installmentsAmount,
      installmentsCount,
      installmentsTotal,
      premium,
      processingFee,
      covers,
      multiProductPremiums,
      insurerLogo,
      policyStartDate,
      primaryProductTitle,
    },
    paymentPeriod = "monthly",
  } = props;
  const isMonthly = paymentPeriod === "monthly";
  const hasInstallments = installmentsAvailable && installmentsAmount;
  const productTitle =
    primaryProductTitle || transformCoverage(covers)[0]?.displayName;
  const totalPrice = isMonthly ? installmentsTotal : premium;
  const processingFeeDetailsText = processingFee
    ? `(inc. tax + ${processingFee} processing fee)`
    : "(inc. tax + fees)";
  const { snowplow } = useSnowplowContext();

  return (
    <Box
      className="mobius-payment-info --has-divider"
      ref={ref}
      data-testid="payment-information"
    >
      <Text className="mobius-payment-info__title" variant="h3">
        Payment Information
      </Text>
      <QuoteCardBadges>
        <QuoteCardBadge data-testid="quote-card-badge-12-month-policy">
          12 month policy
        </QuoteCardBadge>
        <QuoteCardBadge>
          <div
            data-testid="quote-card-badge-cancel-anytime-payment-information"
            className="mobius-quote-card-cancel-anytime__cancel-anytime-container"
          >
            <CancelAnytime
              eventTrackerCallback={() =>
                snowplow?.trigger("cancelAnytimeBadgeClicked", {
                  label: "cancel_anytime_qdp",
                })
              }
            />
          </div>
        </QuoteCardBadge>
      </QuoteCardBadges>
      {multiProductPremiums ? (
        <>
          <ProductInfo
            logo={insurerLogo}
            productTitle="General Liability"
            premium={multiProductPremiums?.generalLiability}
            policyStartDate={policyStartDate}
          />
          <ProductInfo
            logo={insurerLogo}
            productTitle="Professional Liability"
            premium={multiProductPremiums?.professionalLiability}
            policyStartDate={policyStartDate}
          />
        </>
      ) : (
        <ProductInfo
          logo={insurerLogo}
          productTitle={productTitle}
          premium={totalPrice}
          policyStartDate={policyStartDate}
        />
      )}
      <Flex flexDirection="column">
        <div className="mobius-payment-info__total-outer-container">
          <>
            <Flex
              justifyContent="space-between"
              gap="xs"
              className="mobius-payment-info__total-today"
              flexGrow={1}
            >
              <span>To pay today</span>
              <span>
                {isMonthly && hasInstallments ? downpaymentAmount : premium}
              </span>
            </Flex>
            {isMonthly && hasInstallments && (
              <Flex
                justifyContent="space-between"
                gap="xs"
                className="mobius-payment-info__installments"
                flexGrow={1}
              >
                <span>{installmentsCount} monthly payments of</span>
                <span>{installmentsAmount}</span>
              </Flex>
            )}
          </>
          <Segment colour="subtle" className="mobius-payment-info__total">
            <Flex justifyContent="space-between" gap="xs" flexGrow={1}>
              <span>
                <strong>Total</strong> {processingFeeDetailsText}
              </span>
              <span>
                <strong data-testid="payment-information-total">
                  {isMonthly && installmentsTotal ? installmentsTotal : premium}
                </strong>
              </span>
            </Flex>
          </Segment>
        </div>
      </Flex>
    </Box>
  );
};

PaymentInformation.displayName = "PaymentInformation";
